import { isFinite } from "lodash";
import memoize from "lodash.memoize";
import { ReadonlyURLSearchParams } from "next/navigation";
import { isNote } from "../constants/notes";
import {
  isPredefinedRangeType,
  predefinedRanges,
  Range,
} from "../constants/ranges";
import { ScaleType } from "../constants/scales";
import { isVocalRegistor, VocalRegister } from "../constants/vocalRegister";
import convertMelodyRangeScaleToInternalState from "./useExternalState/convertMelodyRangeScaleToInternalState";
import { PlayerExternalState } from "./useExternalState/useExternalState";

export enum ExternalStateSearchParams {
  Bpm = "bpm",

  Range = "range",
  RangeStart = "range_start",
  RangeEnd = "range_end",

  ShorthandMelody = "shorthand_melody",

  VocalRegister = "vocal_register",
}

export interface SearchParams {
  [ExternalStateSearchParams.Bpm]?: string | null;

  [ExternalStateSearchParams.Range]?: string | null;
  [ExternalStateSearchParams.RangeStart]?: string | null;
  [ExternalStateSearchParams.RangeEnd]?: string | null;

  [ExternalStateSearchParams.ShorthandMelody]?: string | null;

  [ExternalStateSearchParams.VocalRegister]?: string | null;
}

/**
 * This is done so that we can memoize the function
 */
export function searchParamsToDefaultProps(
  searchParams: ReadonlyURLSearchParams | null
): SearchParams {
  if (!searchParams) {
    return {};
  }

  return {
    [ExternalStateSearchParams.Bpm]: searchParams.get(
      ExternalStateSearchParams.Bpm
    ),

    [ExternalStateSearchParams.Range]: searchParams.get(
      ExternalStateSearchParams.Range
    ),
    [ExternalStateSearchParams.RangeStart]: searchParams.get(
      ExternalStateSearchParams.RangeStart
    ),
    [ExternalStateSearchParams.RangeEnd]: searchParams.get(
      ExternalStateSearchParams.RangeEnd
    ),

    [ExternalStateSearchParams.ShorthandMelody]: searchParams.get(
      ExternalStateSearchParams.ShorthandMelody
    ),
  };
}

export function playerToSearchParams(
  player: PlayerExternalState
): SearchParams {
  return {
    [ExternalStateSearchParams.Bpm]: player.bpm.toString(),
    [ExternalStateSearchParams.Range]: player.range?.range,
    [ExternalStateSearchParams.RangeStart]: player.range?.low,
    [ExternalStateSearchParams.RangeEnd]: player.range?.high,
    [ExternalStateSearchParams.ShorthandMelody]: player.shorthands?.melody,
  };
}

export default memoize(
  function getDefaultExternalState(
    searchParams: SearchParams,
    passedInDefaultState: Partial<PlayerExternalState> = {}
  ): PlayerExternalState {
    const range = validateRange(searchParams);
    const shorthands = validateShorthands(searchParams);
    const vocalRegister = validateVocalRegister(searchParams);
    const bpm = validateBpm(searchParams);
    const { scales, scaleIndex } = convertMelodyRangeScaleToInternalState({
      range,
      scaleType: defaultScaleType,
      shorthands,
      vocalRegister,
    });
    const activeScale = scales[scaleIndex];
    const defaultState: PlayerExternalState = {
      state: "stopped",
      activeMelodyDegrees: new Set(),
      activeChordDegrees: new Set(),
      activeScale,
      holdScale: false,
      transpositionPattern: "up/down",
      volume: {
        metronome: -6,
        chord: -12,
        melody: -10,
        ...passedInDefaultState.volume,
      },
      bpm,
      shorthands: passedInDefaultState.shorthands ?? shorthands,
      range,
      scaleType: defaultScaleType,
      vocalRegister,
      ...passedInDefaultState,
    };

    return defaultState;
  },
  (params) => Object.keys(params).join(",") + Object.values(params).join(",")
);

export const defaultBpm = 120;
const defaultRange: Range | undefined = undefined;
const defaultScaleType = ScaleType.Major;
const defaultShortHands = undefined;
const defaultVocalRegister: VocalRegister = VocalRegister.Full;

/**
 * Search Param Validators
 */

function validateBpm(
  params: Pick<SearchParams, ExternalStateSearchParams.Bpm> | undefined | null
): number {
  if (!params) {
    return defaultBpm;
  }

  const { bpm } = params;

  if (!bpm) {
    return defaultBpm;
  }

  const parsed = parseInt(bpm, 10);

  if (isNaN(parsed)) {
    return defaultBpm;
  }

  if (!isFinite(parsed)) {
    return defaultBpm;
  }

  return parsed;
}

function validateRange(
  params:
    | Pick<
        SearchParams,
        | ExternalStateSearchParams.Range
        | ExternalStateSearchParams.RangeEnd
        | ExternalStateSearchParams.RangeStart
      >
    | undefined
    | null
): Range | undefined {
  if (!params) {
    return defaultRange;
  }

  let { range, range_end, range_start } = params;

  if (!range && !range_start && !range_end) {
    return defaultRange;
  }

  if (range && isPredefinedRangeType(range)) {
    return predefinedRanges[range];
  }

  if (!range_start || !range_end) {
    return defaultRange;
  }

  if (typeof range_start === "string" && typeof range_end === "string") {
    range_start = range_start.toLowerCase().trim();
    range_end = range_end.toLowerCase().trim();

    if (isNote(range_start) && isNote(range_end)) {
      return {
        range: "custom",
        low: range_start,
        high: range_end,
      };
    }
  }

  return defaultRange;
}

/**
 * TODO: will need to support chords here as well
 */
function validateShorthands(
  params:
    | Pick<SearchParams, ExternalStateSearchParams.ShorthandMelody>
    | undefined
    | null
): PlayerExternalState["shorthands"] {
  if (!params) {
    return defaultShortHands;
  }

  let { shorthand_melody } = params;

  if (typeof shorthand_melody !== "string") {
    return defaultShortHands;
  }

  shorthand_melody = shorthand_melody.trim();

  if (shorthand_melody.length > 0) {
    return { melody: shorthand_melody };
  }

  return defaultShortHands;
}

function validateVocalRegister(
  params:
    | Pick<SearchParams, ExternalStateSearchParams.VocalRegister>
    | undefined
    | null
): VocalRegister {
  if (!params) {
    return defaultVocalRegister;
  }

  let { vocal_register } = params;

  if (typeof vocal_register !== "string") {
    return defaultVocalRegister;
  }

  vocal_register = vocal_register.trim();

  if (isVocalRegistor(vocal_register)) {
    return vocal_register;
  }

  return defaultVocalRegister;
}
